import { createApiRef } from '@backstage/core-plugin-api';
import {
  BuildStatus,
  CurrentTags,
  DeployStatus,
  GroupE2eResults,
  PrFileDiff,
  PrSummary,
  PrType,
  TerraformPlan,
} from './types';

export interface ListPrsOptions {
  componentName: string;
  types?: PrType[];
}

export interface ListPrsByGroupOptions {
  groupName: string;
  types?: PrType[];
}

export interface ComponentPrs {
  componentName: string;
  projectName: string;
  /** ArgoCD-style deploy wave (default 0); deploy waves run in ascending order. */
  wave: number;
  /**
   * Deployment groups the component belongs to (["default"] when the component
   * has no deploy-groups annotation). The group view operates one deployment
   * group at a time; a component may appear in several.
   */
  deployGroups: string[];
  /**
   * True when the component's feature development has stopped (annotation
   * mergeops.kworkinc.com/maintenance-mode: "true"); the group view badges
   * these components. Optional so older backends keep working.
   */
  maintenanceMode?: boolean;
  items: PrSummary[];
  currentTags: CurrentTags;
}

export interface ComponentPrsResponse {
  projectName: string;
  items: PrSummary[];
  currentTags: CurrentTags;
  /**
   * True when the project is managed by Go mergeops (deploypr): a
   * manifests/<project>/pipeline.yaml exists, and promote/hotfix dispatch the
   * deploypr workflows instead of the legacy ones.
   */
  deployprManaged?: boolean;
}

export interface GroupPrs {
  group: string;
  components: ComponentPrs[];
}

export interface ApproveOrMergeResult {
  ok: true;
  reviewId?: number;
  sha?: string;
  /**
   * The PR was already merged, so no merge ran and nothing new was triggered.
   * GitHub answers 200 when merging a merged PR, so the backend checks up front
   * and reports it here rather than letting it pass as a fresh merge.
   */
  alreadyMerged?: boolean;
  /**
   * Server-side lower bound for the build/deploy this merge triggered: the
   * backend clock just before the merge ran, or GitHub's merge time for an
   * already-merged PR. Preferred over the browser clock as the Cloud Build
   * `since` filter — client clock skew otherwise excludes the build forever.
   */
  mergedAt?: string | null;
}

export interface MergeOpsApi {
  listPrs(opts: ListPrsOptions): Promise<ComponentPrsResponse>;
  listPrsByGroup(opts: ListPrsByGroupOptions): Promise<GroupPrs>;
  approvePr(
    componentName: string,
    prNumber: number,
  ): Promise<ApproveOrMergeResult>;
  mergePr(
    componentName: string,
    prNumber: number,
    title?: string,
  ): Promise<ApproveOrMergeResult>;
  /** Record a human's decision to skip one (component, env) in a deploy run. */
  skipDeploy(
    componentName: string,
    body: { env: 'stg' | 'prd'; wave: number; state: string; reason: string },
  ): Promise<{ ok: true }>;
  /** Trigger the MergeOps cherry-pick workflow_dispatch (source PR → target branch). */
  triggerCherryPick(
    componentName: string,
    prNumber: number,
    to: string,
  ): Promise<{ ok: true }>;
  /**
   * Trigger the MergeOps promote workflow_dispatch (stg → prd). The backend
   * picks the legacy or the deploypr workflow by the project's deploy system
   * and returns the dispatched workflow file.
   */
  triggerPromote(
    componentName: string,
  ): Promise<{ ok: true; workflow?: string }>;
  /**
   * Trigger the deploypr hotfix workflow_dispatch: opens a hotfix PR on the
   * release branch holding the stage's running version. Deploypr-managed
   * components only (legacy components hotfix via cherry-pick).
   */
  triggerHotfix(
    componentName: string,
    stage: 'stg' | 'prd',
  ): Promise<{ ok: true; workflow?: string }>;
  /**
   * Latest run_kit Cloud Build status for a component, optionally restricted to
   * builds created at/after `since` (ISO) — used to poll progress after merging
   * build (tag) PRs.
   */
  getBuildStatus(componentName: string, since?: string): Promise<BuildStatus>;
  /**
   * Latest Cloud Run deploy status for a component's env, optionally restricted
   * to revisions created at/after `since` (ISO) — used to poll deploy progress
   * after merging deploy PRs and to gate wave-by-wave deploys.
   */
  getDeployStatus(
    componentName: string,
    env: 'stg' | 'prd',
    since?: string,
  ): Promise<DeployStatus>;
  /**
   * Build status for many of a group's components in one request (one Cloud
   * Build list call), keyed by component name — used by the group wave loops so
   * polling doesn't issue one request per component each tick.
   */
  getGroupBuildStatuses(
    groupName: string,
    componentNames: string[],
    since?: string,
  ): Promise<Record<string, BuildStatus>>;
  /** Deploy status for many of a group's components in one request, keyed by name. */
  getGroupDeployStatuses(
    groupName: string,
    componentNames: string[],
    env: 'stg' | 'prd',
    since?: string,
  ): Promise<Record<string, DeployStatus>>;
  /**
   * Latest E2E results (kwdp mart) for the group's products. Fetched once per
   * view + manual reload — the mart updates hourly, and the group page
   * re-renders at 1 Hz during deploys, so never call this from a poll loop.
   */
  getGroupE2eResults(groupName: string): Promise<GroupE2eResults>;
  /**
   * One PR's current summary, read live (no backend list cache, no search
   * index) — backs the per-row Refresh when a check has finished but the listed
   * status still says pending.
   */
  getPr(componentName: string, prNumber: number): Promise<PrSummary>;
  /** File diffs for a PR, fetched on demand when a row's diff is expanded. */
  getPrFiles(componentName: string, prNumber: number): Promise<PrFileDiff[]>;
  /** Terraform plan(s) for a PR (tfez plan comments), fetched on expand. */
  getPrPlans(componentName: string, prNumber: number): Promise<TerraformPlan[]>;
}

export const mergeOpsApiRef = createApiRef<MergeOpsApi>({
  id: 'plugin.mergeops.api',
});
